import { ThreadTurnPrompt } from "./ThreadTurnPrompt";
import {
  buildPinSegments,
  hasDisplayableContent,
  turnIsLive,
} from "./Thread.utils";
import type {
  MessageEdge,
  MessageItem,
  ThreadMessagesConnection,
} from "./Thread.types";

/** A record the user attached to the message they just sent. */
export type PendingContextReference = {
  id: string;
  identifier: string;
  name: string;
  typename: string;
};

type ThreadMessageListProps = {
  messages: ThreadMessagesConnection | null | undefined;
  isWorkflowExecutionThread: boolean;
  isStreaming: boolean;
  awaitingAgentReply: boolean;
  /** The turn the user just sent, not yet echoed back by the server. */
  pendingPrompt?: string | null;
  /** The records that turn was sent about, shown with it. */
  pendingContext?: PendingContextReference[];
  /** The names of the files that turn carried, shown with it. */
  pendingFiles?: string[];
  /** The user stopped this thread's turn; nothing on it is live any more. */
  stopped?: boolean;
  /** What the thread is laid over; a pinned prompt paints its band in it. */
  stickyBackdropColor?: string;
};

export function ThreadMessageList({
  messages,
  isWorkflowExecutionThread,
  isStreaming,
  awaitingAgentReply,
  pendingPrompt,
  pendingContext = [],
  pendingFiles = [],
  stopped = false,
  stickyBackdropColor,
}: ThreadMessageListProps) {
  const visibleEdges: MessageEdge[] = messages?.edges ?? [];

  const nodes = visibleEdges.map((e) => e.node);
  const lastUserIndex = nodes.reduce(
    (found, m, i) => (m.account != null ? i : found),
    -1,
  );
  const lastAgentIndex = nodes.reduce(
    (found, m, i) => (m.account == null ? i : found),
    -1,
  );
  const pinSegments = buildPinSegments(visibleEdges);
  // A reply is on its way and nothing has been drawn for it yet: the prompt is
  // still in flight, or the server has echoed it but not made the reply row.
  const awaitingReplyTurn =
    awaitingAgentReply &&
    !isWorkflowExecutionThread &&
    !stopped &&
    (pendingPrompt != null || lastUserIndex > lastAgentIndex);

  function resolveLoading(
    index: number,
    message: MessageItem,
    isUser: boolean,
  ): boolean {
    if (!awaitingAgentReply) return false;
    // Do not flash the loading skeleton on the streaming agent row; partial network
    // cache reads briefly look empty between stream upserts.
    if (isStreaming && !isUser && index === lastAgentIndex) return false;
    if (isWorkflowExecutionThread) {
      return (
        index === visibleEdges.length - 1 &&
        (isUser || !hasDisplayableContent(message))
      );
    }
    if (!isUser) {
      return index === lastAgentIndex && !hasDisplayableContent(message);
    }
    const hasAgentAfter = nodes
      .slice(index + 1)
      .some((m) => m.account == null && hasDisplayableContent(m));
    return !hasAgentAfter && index === lastUserIndex;
  }

  return (
    <>
      {pinSegments.map((segment, segmentIndex) => {
        const segmentKey =
          segment.edges[0]?.edge.node.identifier ??
          segment.edges[0]?.edge.node.id ??
          `segment-${segmentIndex}`;

        return (
          <div
            key={segmentKey}
            className="flex flex-col shrink-0 overflow-visible w-full min-w-0 [overflow-anchor:none]"
          >
            {segment.edges.map(({ edge, index }) => {
              const message = edge.node;
              const isUser = message.account != null;
              const isPinned = segment.pinHeaderIndex === index;
              const loading = resolveLoading(index, message, isUser);
              const isLastEdge = index === visibleEdges.length - 1;
              const messageKey =
                message.identifier ?? message.id ?? `message-${index}`;

              return (
                <ThreadTurnPrompt
                  key={messageKey}
                  message={message}
                  loading={loading}
                  turnIndex={index}
                  isPinned={isPinned}
                  isStreaming={isLastEdge && isStreaming}
                  stickyBackdropColor={stickyBackdropColor}
                  turnState={
                    stopped && index === lastAgentIndex
                      ? "stopped"
                      : turnIsLive(message, stopped)
                        ? "live"
                        : "ended"
                  }
                />
              );
            })}
          </div>
        );
      })}
      {pendingPrompt ? (
        // Pinned like the row that replaces it: a user turn always heads its
        // own segment, so drawing it any other way would show the prompt
        // without its bubble until the server echoed it back.
        <ThreadTurnPrompt
          message={PENDING_TURN(pendingPrompt, pendingContext, pendingFiles)}
          loading={false}
          turnIndex={visibleEdges.length}
          isPinned
          stickyBackdropColor={stickyBackdropColor}
        />
      ) : null}
      {awaitingReplyTurn ? (
        // The agent's side of the turn from the moment it is sent, working,
        // instead of appearing once the server has made its reply row.
        <ThreadTurnPrompt
          message={PENDING_REPLY()}
          loading={false}
          turnIndex={visibleEdges.length + (pendingPrompt ? 1 : 0)}
          isPinned={false}
          isStreaming
          turnState="live"
          stickyBackdropColor={stickyBackdropColor}
        />
      ) : null}
    </>
  );
}

/** The agent's reply, before the server has made the row it will stream into. */
const PENDING_REPLY = (): MessageItem =>
  ({
    __typename: "MessageObject",
    id: "pending-reply",
    identifier: "pending-reply",
    createdAt: new Date().toISOString(),
    account: null,
    content: [],
    context: [],
    execution: null,
    timeSeriesData: null,
  }) as unknown as MessageItem;

/**
 * A display-only stand-in for the turn in flight.
 *
 * It is built during render and thrown away with it, so it can never be
 * merged, sorted, or left behind in the cache the way a written row could.
 */
const PENDING_TURN = (
  text: string,
  references: PendingContextReference[],
  files: string[],
): MessageItem =>
  ({
    __typename: "MessageObject",
    id: "pending-turn",
    identifier: "pending-turn",
    createdAt: new Date().toISOString(),
    account: {
      __typename: "AccountObject",
      id: "pending",
      identifier: "pending",
    },
    content: [
      {
        __typename: "MessageContentObject",
        id: "pending-turn-text",
        identifier: "pending-turn-text",
        type: "text",
        status: "done",
        text,
        toolName: null,
        toolInput: null,
        toolOutput: null,
        toolError: null,
        parentIdentifier: null,
        executionMode: null,
        iterationIndex: null,
        pendingChange: null,
        nodes: [],
        context: [],
      },
    ],
    context: [],
    references: references.map((reference) => ({
      __typename: "MessageContextObject",
      id: `pending-${reference.id}`,
      identifier: `pending-${reference.identifier}`,
      node: {
        __typename: reference.typename,
        id: reference.id,
        identifier: reference.identifier,
        name: reference.name,
      },
    })),
    files: files.map((name, index) => ({
      __typename: "MessageFileObject",
      id: `pending-file-${index}`,
      identifier: `pending-file-${index}`,
      name,
    })),
    execution: null,
    timeSeriesData: null,
  }) as unknown as MessageItem;
